import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, catchError, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { Site } from 'src/app/bo/site';
import { RapportCreationModaleComponent } from 'src/app/components/rapport-creation-modale/rapport-creation-modale.component';
import { DeliveryActivityService } from 'src/app/services/delivery-activity.service';
import { DEFAULT_SHIFT_CONFIG } from 'src/app/services/drill-activity.service';
import { HttpService } from 'src/app/services/http.service';
import { FeatureFlagService } from 'src/app/services/new_services/feature-flag.service';
import { ShiftConfigService } from 'src/app/services/shift-config.service';
import { DeliveryActivityItem, DeliveryBlastRow, SelectedShiftColumn } from 'src/app/shared/models/delivery-activity.models';
import { ShiftConfig, shiftConfigFromDto } from 'src/app/shared/models/drilling.models';
import { DrillColorBy, PeriodBounds, TimeScale } from '../planning.types';
import { ShiftColumn, generateColumns, toIsoDateString } from '../utils/column-generator';
import { DeliveryGridComponent } from './ui/delivery-grid/delivery-grid.component';

interface SiteContext {
  siteId: number;
  config: ShiftConfig;
  timezone?: string;
}

/**
 * Planning des livraisons : charge les livraisons du site sur la période reçue,
 * les affiche dans la grille et gère la sélection de shifts qui alimente le
 * rapport de livraison. La barre de contrôles appartient au conteneur.
 */
@Component({
  selector: 'app-delivery-planning',
  standalone: true,
  templateUrl: './delivery-planning.component.html',
  styleUrls: ['./delivery-planning.component.scss'],
  imports: [TranslateModule, DeliveryGridComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryPlanningComponent {
  readonly siteId    = input.required<number>();
  readonly bounds    = input.required<PeriodBounds>();
  readonly colorBy   = input<DrillColorBy>('rig');
  readonly timeScale = input<TimeScale>('week');

  private readonly deliveryActivity   = inject(DeliveryActivityService);
  private readonly shiftConfigService = inject(ShiftConfigService);
  private readonly http               = inject(HttpService);
  private readonly featureFlags       = inject(FeatureFlagService);
  private readonly modals             = inject(BsModalService);
  private readonly router             = inject(Router);
  private readonly destroyRef         = inject(DestroyRef);

  protected readonly shiftConfig = signal<ShiftConfig>(DEFAULT_SHIFT_CONFIG);
  protected readonly rows        = signal<DeliveryBlastRow[]>([]);
  protected readonly loading     = signal(false);

  private readonly rawSelection = signal<ReadonlySet<string>>(new Set<string>());

  protected readonly shiftColumns = computed<ShiftColumn[]>(() =>
    generateColumns(this.bounds().start, this.bounds().end, this.shiftConfig()),
  );

  /** Les shifts sélectionnés hors de la période affichée sont ignorés, comme dans l'écran d'origine. */
  protected readonly selectedShiftIds = computed<ReadonlySet<string>>(() => {
    const raw = this.rawSelection();
    return new Set(this.shiftColumns().filter(col => raw.has(col.id)).map(col => col.id));
  });

  private readonly selectedShifts = computed<SelectedShiftColumn[]>(() => {
    const selected = this.selectedShiftIds();
    const config   = this.shiftConfig();

    return this.shiftColumns()
      .filter(col => selected.has(col.id))
      .map(col => {
        const startHour = config.shiftStartHours?.[col.shiftIndex] ?? 0;
        const duration  = config.shiftDurationHours;
        return {
          id:              col.id,
          date:            col.date,
          shiftIndex:      col.shiftIndex,
          shiftLabel:      col.shiftLabel,
          startHour,
          endHour:         (startHour + duration) % 24,
          crossesMidnight: startHour + duration >= 24,
        };
      });
  });

  private readonly selectedDeliveryIds = computed<string[]>(() => {
    const selected = this.selectedShiftIds();
    if (selected.size === 0) { return []; }

    const ids = new Set<string>();
    for (const row of this.rows()) {
      for (const d of row.deliveries) {
        if (selected.has(`${d.date}_${d.shiftIndex}`)) { ids.add(d.id); }
      }
    }
    return [...ids];
  });

  readonly selectedShiftCount    = computed(() => this.selectedShiftIds().size);
  readonly selectedDeliveryCount = computed(() => this.selectedDeliveryIds().length);
  readonly canReport             = computed(() => this.selectedDeliveryCount() > 0);

  constructor() {
    const bounds$ = toObservable(this.bounds);

    // Même chaîne que le forage : changer de site annule le contexte et les lignes
    // en vol, changer de période n'annule que les lignes.
    toObservable(this.siteId)
      .pipe(
        tap(() => {
          this.loading.set(true);
          this.shiftConfig.set(DEFAULT_SHIFT_CONFIG);
          this.rawSelection.set(new Set<string>());
        }),
        switchMap(siteId => this.loadSiteContext(siteId)),
        tap(context => this.shiftConfig.set(context.config)),
        switchMap(context => bounds$.pipe(
          tap(() => this.loading.set(true)),
          switchMap(bounds => this.deliveryActivity
            .getDeliveryRowsForWeek(context.siteId, bounds, context.config, context.timezone)
            .pipe(catchError(() => of([] as DeliveryBlastRow[])))),
        )),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(rows => {
        this.rows.set(rows);
        this.loading.set(false);
      });
  }

  /**
   * La config de shifts fige la colonne de chaque livraison au chargement et le
   * fuseau du site son jour local : les deux doivent précéder la répartition.
   */
  private loadSiteContext(siteId: number): Observable<SiteContext> {
    return forkJoin({
      config: this.shiftConfigService.getShiftConfig(siteId).pipe(
        map(dto => shiftConfigFromDto(dto)),
        catchError(() => of(DEFAULT_SHIFT_CONFIG)),
      ),
      timezone: this.http.get<Site>(`Sites/${siteId}`).pipe(
        map(site => site?.timezone ?? undefined),
        catchError(() => of(undefined)),
      ),
    }).pipe(map(({ config, timezone }) => ({ siteId, config, timezone })));
  }

  protected toggleShift(col: ShiftColumn): void {
    const next = new Set(this.selectedShiftIds());
    if (next.has(col.id)) { next.delete(col.id); } else { next.add(col.id); }
    this.rawSelection.set(next);
  }

  /** Ouvre la modale de rapport legacy en mode activité, sur les livraisons des shifts sélectionnés. */
  openReport(): void {
    const deliveryIds = this.selectedDeliveryIds();
    if (deliveryIds.length === 0) { return; }

    const { start, end } = this.bounds();
    const shifts         = this.selectedShifts();
    const selectedDates  = [...new Set(shifts.map(shift => shift.date))];

    this.modals.show(RapportCreationModaleComponent, {
      backdrop:            true,
      ignoreBackdropClick: true,
      keyboard:            false,
      class:               'modal-dialog-centered old-theme',
      initialState: {
        activityReportContext: signal({
          siteId:            this.siteId(),
          startDate:         start.toISOString(),
          endDate:           end.toISOString(),
          selectedDates,
          selectedShifts:    shifts,
          deliveryIds,
          defaultReportName: `Delivery_reports_${toIsoDateString(start)}_${toIsoDateString(end)}`,
        }),
      } as Partial<RapportCreationModaleComponent>,
    });
  }

  /** Ouvre le tir dans l'écran site actif pour ce site, sur la vue livraison de l'onglet Holes. */
  protected openBlast(row: DeliveryBlastRow): void {
    const siteId = this.siteId();
    if (this.featureFlags.isLegacySiteEnabled(siteId)) {
      this.router.navigate(
        ['mysite', siteId, 'myprojects', row.projectId],
        { queryParams: { blastId: row.blastId, tab: 'deliveries' } },
      );
      return;
    }
    this.router.navigate(['/site', siteId], {
      queryParams: {
        projectId: row.projectId,
        blastId:   row.blastId,
        nodeType:  'blast',
        nodeValue: row.label,
        tab:       'holes',
        view:      'delivery',
      },
    });
  }

  protected openDelivery(delivery: DeliveryActivityItem): void {
    const row = this.rows().find(r => r.blastId === delivery.blastId);
    if (row) { this.openBlast(row); }
  }
}
